import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { postData } from './api';

type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface AiChatState {
  feed: unknown[];
  selectedOptions: unknown[];
  suggestions: unknown;
  status: AsyncStatus;
  error: string | null;
  isOpen: boolean;
}

const initialState: AiChatState = {
  feed: [],
  selectedOptions: [],
  suggestions: null,
  status: 'idle',
  error: null,
  isOpen: false,
};

export const fetchAiSuggestion = createAsyncThunk<unknown, any>(
  'aiChat/fetchAiSuggestion',
  async (params, { rejectWithValue }) => {
    try {
      let emotions: unknown[] = [];
      let text = '';
      if (params && typeof params === 'object' && !Array.isArray(params)) {
        emotions = Array.isArray(params.emotions) ? params.emotions : (params.emotions ? [params.emotions] : []);
        text = params.text || '';
      } else {
        emotions = Array.isArray(params) ? params : [params];
      }
      const body = { limit: 3, emotions, text };
      const result = await postData({ endpoint: 'enrichment_center/get_ai_suggestions', payload: body });
      return { data: result };
    } catch (err: any) {
      if (err.response) return rejectWithValue(err.response.data || 'Error fetching AI suggestions');
      return rejectWithValue(err.message || 'Network error');
    }
  }
);

const aiChatSlice = createSlice({
  name: 'aiChat',
  initialState,
  reducers: {
    setFeed(state, action: PayloadAction<unknown[]>) {
      state.feed = action.payload || [];
    },
    addMessage(state, action: PayloadAction<unknown>) {
      state.feed = [...state.feed, action.payload];
    },
    clearFeed(state) {
      state.feed = [];
    },
    setSelectedOptions(state, action: PayloadAction<unknown[]>) {
      state.selectedOptions = action.payload || [];
    },
    clearSelectedOptions(state) {
      state.selectedOptions = [];
    },
    setSuggestions(state, action: PayloadAction<unknown>) {
      state.suggestions = action.payload;
    },
    openAiChat(state) {
      state.isOpen = true;
    },
    closeAiChat(state) {
      state.isOpen = false;
    },
    toggleAiChat(state) {
      state.isOpen = !state.isOpen;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAiSuggestion.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchAiSuggestion.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.suggestions = action.payload;
      })
      .addCase(fetchAiSuggestion.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) ?? action.error.message ?? null;
      });
  },
});

export const { setFeed, addMessage, clearFeed, setSelectedOptions, clearSelectedOptions, setSuggestions, openAiChat, closeAiChat, toggleAiChat } = aiChatSlice.actions;

export const selectAiChatOpen = (state: { aiChat: AiChatState }) =>
  state?.aiChat?.isOpen ?? false;

export default aiChatSlice.reducer;
